import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError, type Desempenho } from "../api";
import ErrorState from "./ErrorState";
import { ordenarPorNome } from "../ordem";
import { IconRefresh, IconSearch } from "../icons";

type Aba = "equipes" | "colaboradores" | "servicos" | "retrabalhos";

const ABAS: { key: Aba; label: string }[] = [
  { key: "equipes", label: "Por equipe" },
  { key: "colaboradores", label: "Por colaborador" },
  { key: "servicos", label: "Serviços avaliados" },
  { key: "retrabalhos", label: "Retrabalho por serviço" },
];

/** AAAA-MM-DD → DD/MM/AAAA (sem passar por Date, para não escorregar de fuso). */
function dataBR(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [a, m, d] = String(iso).slice(0, 10).split("-");
  return a && m && d ? `${d}/${m}/${a}` : String(iso);
}

const moeda = (v: number) =>
  v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 2 });

/** Cor da nota média: 8+ ok, 6–8 atenção, abaixo disso crítico. */
function tomNota(nota: number | null): string {
  if (nota == null) return "neutral";
  if (nota >= 8) return "ok";
  if (nota >= 6) return "warn";
  return "danger";
}

/**
 * Satisfação e retrabalho DA EXECUÇÃO, dentro da Qualidade.
 *
 * Complementa (não substitui) as abas "Satisfação do Cliente" e "Retrabalho"
 * daqui: aquelas registram o indicador do DEPARTAMENTO (período, NPS, canal,
 * horas); esta mostra CADA SERVIÇO EXECUTADO e a média por colaborador e por
 * equipe, lidos da Gestão da Operação/Planejamento. Nada é copiado — os números
 * vêm da origem a cada abertura.
 */
export default function DesempenhoEquipeView() {
  const [aba, setAba] = useState<Aba>("equipes");
  const [dados, setDados] = useState<Desempenho | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [busca, setBusca] = useState("");

  const carregar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDados(await api.desempenho());
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(String(err)));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const filtro = busca.trim().toLowerCase();
  const casa = (...campos: unknown[]) =>
    !filtro || campos.some((c) => String(c ?? "").toLowerCase().includes(filtro));

  // Equipes e colaboradores em ordem alfabética (garantida aqui, não na API).
  // Serviços e retrabalhos continuam na ordem da origem (por data).
  const equipes = useMemo(
    () => ordenarPorNome((dados?.porEquipe ?? []).filter((e) => casa(e.equipe)), (e) => e.equipe),
    [dados, filtro],
  );
  const colaboradores = useMemo(
    () =>
      ordenarPorNome(
        (dados?.porColaborador ?? []).filter((c) => casa(c.nome, c.equipe, c.cargo)),
        (c) => c.nome,
      ),
    [dados, filtro],
  );
  const servicos = useMemo(
    () => (dados?.servicos ?? []).filter((s) => casa(s.cliente, s.obra, s.responsavel, s.equipe)),
    [dados, filtro],
  );
  const retrabalhos = useMemo(
    () => (dados?.retrabalhos ?? []).filter((r) => casa(r.obra, r.responsavel, r.equipe, r.descricao, r.motivo)),
    [dados, filtro],
  );

  if (error) return <ErrorState message={error.message} code={error.code} onRetry={carregar} />;

  // A integração respondeu (as pessoas chegaram), mas ninguém lançou avaliação
  // ainda. Sem dizer isso, a tela de zeros parece defeito de integração.
  const semLancamento =
    !loading &&
    !!dados &&
    (dados.porColaborador?.length ?? 0) > 0 &&
    (dados.servicos?.length ?? 0) === 0 &&
    (dados.retrabalhos?.length ?? 0) === 0;

  return (
    <div className="crud">
      <div className="crud-aviso">
        Esta tela mostra a satisfação e o retrabalho <strong>de cada serviço executado</strong>, com a
        média por colaborador e por equipe — os dados são lidos da{" "}
        <strong>Gestão da Operação / Planejamento</strong> e não são gravados aqui. As abas
        "Satisfação do Cliente" e "Retrabalho" da Qualidade continuam sendo o indicador do
        departamento (período, NPS, canal, horas).
      </div>

      {semLancamento && (
        <div className="crud-aviso">
          A ligação com a Gestão da Operação está funcionando — as{" "}
          <strong>{dados?.porColaborador.length} pessoas</strong> e as equipes abaixo vieram de lá
          agora. O que ainda não existe é <strong>lançamento</strong>: assim que uma avaliação de
          serviço for registrada na aba <strong>Satisfação</strong> (ou um retrabalho) da Gestão da
          Operação, a nota aparece aqui e entra na média do painel de indicadores.
        </div>
      )}

      <div className="crud-toolbar">
        <div className="almox-tabs">
          {ABAS.map((a) => (
            <button
              key={a.key}
              className={`almox-tab ${aba === a.key ? "active" : ""}`}
              onClick={() => setAba(a.key)}
            >
              {a.label}
            </button>
          ))}
        </div>
        <div className="search-wrap">
          <IconSearch size={17} />
          <input
            className="search"
            placeholder="Filtrar por equipe, colaborador, cliente ou obra..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
        <div className="toolbar-right">
          <button className="btn-secondary" onClick={carregar} title="Atualizar">
            <IconRefresh size={16} /> Atualizar
          </button>
        </div>
      </div>

      <div className="table-wrap">
        {loading ? (
          <div className="empty">Carregando…</div>
        ) : aba === "equipes" ? (
          equipes.length === 0 ? (
            <div className="empty">Nenhuma equipe encontrada.</div>
          ) : (
            <table className="table table-compact">
              <thead>
                <tr>
                  <th>Equipe</th>
                  <th>Pessoas</th>
                  <th>Serviços avaliados</th>
                  <th>Satisfação média</th>
                  <th>Retrabalho (ocorrências)</th>
                  <th>Custo do retrabalho</th>
                </tr>
              </thead>
              <tbody>
                {equipes.map((e) => (
                  <tr key={e.equipe}>
                    <td>
                      <strong>{e.equipe}</strong>
                    </td>
                    <td>{e.colaboradores}</td>
                    <td>{e.satisfacaoQtd}</td>
                    <td>
                      {e.satisfacaoMedia == null ? (
                        "—"
                      ) : (
                        <span className={`pill pill-${tomNota(e.satisfacaoMedia)}`}>
                          {e.satisfacaoMedia.toFixed(1)}
                        </span>
                      )}
                    </td>
                    <td>{e.retrabalhoOcorrencias}</td>
                    <td>{moeda(e.retrabalhoCusto)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : aba === "colaboradores" ? (
          colaboradores.length === 0 ? (
            <div className="empty">Nenhum colaborador encontrado.</div>
          ) : (
            <table className="table table-compact">
              <thead>
                <tr>
                  <th>Colaborador</th>
                  <th>Equipe</th>
                  <th>Serviços avaliados</th>
                  <th>Satisfação média</th>
                  <th>Última avaliação</th>
                  <th>Retrabalho</th>
                  <th>Em aberto</th>
                </tr>
              </thead>
              <tbody>
                {colaboradores.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <strong>{c.nome}</strong>
                      {c.cargo && <br />}
                      {c.cargo && <small>{c.cargo}</small>}
                    </td>
                    <td>{c.equipe || "—"}</td>
                    <td>{c.satisfacaoQtd}</td>
                    <td>
                      {c.satisfacaoMedia == null ? (
                        "—"
                      ) : (
                        <span className={`pill pill-${tomNota(c.satisfacaoMedia)}`}>
                          {c.satisfacaoMedia.toFixed(1)}
                        </span>
                      )}
                    </td>
                    <td>{dataBR(c.satisfacaoUltima)}</td>
                    <td>
                      {c.retrabalhoOcorrencias === 0
                        ? "—"
                        : `${c.retrabalhoOcorrencias} · ${moeda(c.retrabalhoCusto)}`}
                    </td>
                    <td>
                      {c.retrabalhoAbertos > 0 ? (
                        <span className="pill pill-danger">{c.retrabalhoAbertos}</span>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : aba === "servicos" ? (
          servicos.length === 0 ? (
            <div className="empty">Nenhum serviço avaliado ainda.</div>
          ) : (
            <table className="table table-compact">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Cliente</th>
                  <th>Obra / serviço</th>
                  <th>Responsável</th>
                  <th>Equipe</th>
                  <th>Nota</th>
                  <th>Comentário</th>
                </tr>
              </thead>
              <tbody>
                {servicos.map((s) => (
                  <tr key={s.id}>
                    <td>{dataBR(s.data)}</td>
                    <td>{s.cliente || "—"}</td>
                    <td>{s.obra || "—"}</td>
                    <td>{s.responsavel || "—"}</td>
                    <td>{s.equipe || "—"}</td>
                    <td>
                      {s.nota == null ? (
                        "—"
                      ) : (
                        <span className={`pill pill-${tomNota(s.nota)}`}>{s.nota}</span>
                      )}
                    </td>
                    <td>{s.comentario || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : retrabalhos.length === 0 ? (
          <div className="empty">Nenhum retrabalho registrado na execução.</div>
        ) : (
          <table className="table table-compact">
            <thead>
              <tr>
                <th>Data</th>
                <th>Obra / serviço</th>
                <th>Responsável</th>
                <th>Equipe</th>
                <th>Descrição</th>
                <th>Motivo</th>
                <th>Qtd.</th>
                <th>Custo</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {retrabalhos.map((r) => (
                <tr key={r.id}>
                  <td>{dataBR(r.data)}</td>
                  <td>{r.obra || "—"}</td>
                  <td>{r.responsavel || "—"}</td>
                  <td>{r.equipe || "—"}</td>
                  <td>{r.descricao || "—"}</td>
                  <td>{r.motivo || "—"}</td>
                  <td>{r.quantidade}</td>
                  <td>{moeda(r.custo)}</td>
                  <td>
                    <span className={`pill pill-${r.status === "Resolvido" ? "ok" : "warn"}`}>
                      {r.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
